import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import * as _ from "lodash";
import { of } from "rxjs";
import { catchError, map, switchMap, tap, withLatestFrom } from "rxjs/operators";

import { VendorService } from "../../../services/api/vendor.service";
import { OpenActions } from "../../actions/open.actions";
import { FailState } from "../../models/fail-state";
import { VendorRatingState } from "../../models/open/vendor-rating-state";
import { AppState } from "../../reducers";
import { OpenSelectors } from "../../selectors/open.selectors";

@Injectable()
export class VendorRatingsGetEffects {
	get = createEffect(() =>
		this.actions.pipe(
			ofType(OpenActions.vendorRating.get),
			withLatestFrom(this.store.select(OpenSelectors.vendorRatings)),
			switchMap(([action, state]) => {
				if (this.canUseStoreValue(action, state)) {
					const vendorId = action.vendorId;
					const rating = _.get(state.ratings, action.vendorId);
					return of(
						OpenActions.vendorRating.getSuccess({ vendorId, rating, loading: false })
					);
				} else {
					return of(OpenActions.vendorRating.getApi({ ..._.omit(action, "type") }));
				}
			})
		)
	);

	getApi = createEffect(() =>
		this.actions.pipe(
			ofType(OpenActions.vendorRating.getApi),
			switchMap((action) =>
				this.vendorService.vendorRating(action.vendorId).pipe(
					map((rating) =>
						OpenActions.vendorRating.getSuccess({
							vendorId: action.vendorId,
							rating,
							loading: false
						})
					),
					catchError((error) =>
						of(OpenActions.vendorRating.getFail(new FailState(error)))
					)
				)
			)
		)
	);

	getMany = createEffect(
		() =>
			this.actions.pipe(
				ofType(OpenActions.vendorRating.getMany),
				withLatestFrom(this.store.select(OpenSelectors.vendorRatings)),
				tap(([action, state]) => {
					_.forEach(action.vendorIds, (vendorId) => {
						const existingRating = _.get(state, ["ratings", vendorId]);
						if (existingRating) {
							this.store.dispatch(
								OpenActions.vendorRating.getSuccess({
									vendorId,
									rating: existingRating,
									loading: false
								})
							);
						} else {
							this.vendorService.vendorRating(vendorId).subscribe((rating) => {
								this.store.dispatch(
									OpenActions.vendorRating.getSuccess({
										vendorId,
										rating,
										loading: false
									})
								);
							});
						}
					});
				})
			),
		{ dispatch: false }
	);

	constructor(
		private actions: Actions,
		private store: Store<AppState>,
		private vendorService: VendorService
	) {}

	private canUseStoreValue(action, state: VendorRatingState): boolean {
		return (
			!_.has(state, "error") && !state.loading && _.has(state, ["ratings", action.vendorId])
		);
	}
}
